import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { runTask } from 'ember-lifeline';
import { STATUS_KEYS } from 'scaleflow/models/initiative-update';
import capitalize from 'scaleflow/utils/capitalize';
import numberSort from 'scaleflow/utils/numberSort';
import { getOwner } from '@ember/owner';
import type IntlService from 'ember-intl/services/intl';
import type CurrentUserService from 'scaleflow/services/current-user';
import type FeatureAccessService from 'scaleflow/services/feature-access';
import type OrganisationService from 'scaleflow/services/organisation';
import { doNotTrackTenantIds } from './tracker';

/** The slice of the PostHog client (registered as `posthog:client`) used here. */
interface PosthogClient {
  isFeatureEnabled?: (key: string) => boolean;
  setGroupPropertiesForFlags?: (groups: Record<string, unknown>) => void;
  onFeatureFlags?: (callback: (flags: string[]) => void) => void;
  getGroups: () => Record<string, unknown>;
}

interface Option<V = string> {
  label: string;
  value: V;
  description?: string;
  tableOrder?: number;
  disabled?: boolean;
}

interface OrderedOption extends Option {
  tableOrder: number;
}

type PermissionConfig = Record<
  string,
  { enabled?: boolean; tenants?: string[] } | undefined
>;

export default class BetaToggleService extends Service {
  @service('organisation') declare orgService: OrganisationService;
  @service declare intl: IntlService;
  @service('current-user') declare currentUser: CurrentUserService;
  @service('feature-access') declare featureAccess: FeatureAccessService;

  @tracked config: PermissionConfig | null = null;
  @tracked featureFlags: string[] = [];
  @tracked callbackRegistered = false;
  @tracked groupIsSet = false;

  get orgId() {
    return this.orgService.id;
  }

  // Loose comparison on purpose: the id can arrive as the number 2 or the string '2'.
  private get isInternalTenant() {
    return Number(this.orgId) === 2;
  }

  private lookupPosthog() {
    return getOwner(this)!.lookup('posthog:client' as never) as
      PosthogClient | undefined;
  }

  get posthog() {
    const posthog = this.lookupPosthog();
    // If For whatever reason the tracker is not there. We default to features being enabled.
    return (
      posthog ?? {
        isFeatureEnabled: () => true,
      }
    );
  }

  #flagsChanged(newFlags: string[]) {
    return [...this.featureFlags].sort().join() !== [...newFlags].sort().join();
  }

  deferUpdate(callback: () => void) {
    runTask(this, callback);
  }

  ensureFeatureFlagsAreUpToDate() {
    if (this.callbackRegistered) return;
    // We defer this update to ensure that we don't block the main thread and cause Ember to throw a rendering error.
    // Since we update a few tracked variables during a getter call (hack), which can re-trigger the getter and caus an inifinite loop,
    // we employ this deferred update hack
    this.deferUpdate(() => (this.callbackRegistered = true));

    const posthog = this.lookupPosthog();

    /**
   * When we target a feature toggle by a group property like "name", PostHog needs to look up that property value from its servers. This only works if:
    The property has been set via .group() or group_identify
    The property has been successfully sent to and processed by PostHog's servers
    The property is set consistently across all sources (frontend and backend)

    In the eventuality that none of the above are true, PostHog will not be able to determine the value of the property
    and will treat it as undefined, which will cause feature flags targeting that property to not work as expected.
    By setting the group properties for flags explicitly, we ensure that PostHog has the necessary information to evaluate feature flags correctly,
    even if there are delays or issues with processing group properties.

    We choose the 'id' property has unlinke name, it is guaranteed to be set and consistent across both frontend and backend, as it is a core property of the organisation object in our system.
   */
    posthog?.setGroupPropertiesForFlags?.({
      tenant: { id: this.orgService.id },
    });

    if (posthog?.onFeatureFlags) {
      posthog.onFeatureFlags((flags) => {
        const { tenant } = posthog.getGroups();
        if (tenant || doNotTrackTenantIds.includes(this.orgId as string))
          this.deferUpdate(() => (this.groupIsSet = true));
        // Only update if flags actually changed to avoid unnecessary re-renders.
        if (this.#flagsChanged(flags)) {
          this.deferUpdate(() => {
            this.featureFlags = flags;
          });
        }
      });
    }
  }

  isFeatureEnabled(key: string, checkIfSaasCustomer = false) {
    if (this.isInternalTenant) return true;
    const posthog = this.lookupPosthog();
    if (!posthog) return true;
    this.ensureFeatureFlagsAreUpToDate();
    if (checkIfSaasCustomer && !this.isSaasCustomer) {
      return false;
    }
    // Result ignored: the call only makes PostHog send `$feature_flag_called`, so the
    // flag isn't reported as stale. The decision comes from the tracked list above.
    posthog.isFeatureEnabled?.(key);
    return this.featureFlags.includes(key);
  }

  get showCheckin() {
    return this.isFeatureEnabled('check-in', true);
  }

  get showMetrics() {
    return this.isFeatureEnabled('metrics', true);
  }

  get showPlanning() {
    return this.isFeatureEnabled('planning', true);
  }

  get showWorkstreams() {
    return this.isFeatureEnabled('workstreams', true);
  }

  get showKnowledgeBaseFeatures() {
    // Deliberately not gated on `saas-customer`: Xray customers get knowledge
    // base access from this flag alone, so every call site stays a single check.
    return this.isFeatureEnabled('knowledge-base-files-sync');
  }

  get showChat() {
    return this.isFeatureEnabled('show-chat', true);
  }

  get showIdeas() {
    return this.isFeatureEnabled('ideas', true);
  }

  get showGroups() {
    return this.isFeatureEnabled('groups', true);
  }

  get isXrayCustomer() {
    return this.isFeatureEnabled('xray-customer');
  }

  get isSaasCustomer() {
    return this.isFeatureEnabled('saas-customer');
  }

  get hideObservations() {
    if (this.isInternalTenant) return false; // We want to show observations for the internal tenant for testing purposes
    return this.isFeatureEnabled('hide-observations');
  }

  get isPlatformAdmin() {
    return this.featureAccess.isPlatformAdmin;
  }

  get showInitiativeAbbrvFieldInQuickAddFormForQPQ() {
    return this.orgId === '01JWX0S5B0X2Z7RS5EANMYPP1Z';
  }

  get statusOptions() {
    const options: OrderedOption[] = [
      {
        label: this.intl.t('status-options.draft'),
        value: 'DRAFT',
        description: this.intl.t('status-options.draft-description'),
        tableOrder: 4,
      },
      {
        label: this.intl.t('status-options.discovery'),
        value: 'DISCOVERY',
        description: this.intl.t('status-options.discovery-description'),
        tableOrder: 3,
      },
      {
        label: this.intl.t('status-options.planning'),
        value: 'READY FOR PLANNING',
        description: this.intl.t('status-options.planning-description'),
        tableOrder: 2,
      },
      {
        label: this.intl.t('status-options.ready'),
        value: 'READY',
        description: this.intl.t('status-options.ready-description'),
        tableOrder: 1,
      },

      {
        label: this.intl.t('status-options.active'),
        value: 'ACTIVE',
        description: this.intl.t('status-options.active-description'),
        tableOrder: 0,
      },
      {
        label: this.intl.t('status-options.completed'),
        value: 'COMPLETED',
        description: this.intl.t('status-options.completed-description'),
        tableOrder: 6,
      },
      {
        label: this.intl.t('status-options.archived'),
        value: 'ARCHIVED',
        description: this.intl.t('status-options.archived-description'),
        tableOrder: 8,
      },
      {
        label: this.intl.t('status-options.parked'),
        value: 'PARKED',
        description: this.intl.t('status-options.parked-description'),
        tableOrder: 5,
      },
    ];

    if (['01JWX0S5B0X2Z7RS5EANMYPP1Z', '2'].includes(this.orgId as string)) {
      options.push({
        label: this.intl.t('status-options.soft-delete'),
        value: 'SOFT DELETE',
        description: this.intl.t('status-options.soft-delete-description'),
        tableOrder: 12,
      });
    }

    return options;
  }

  get sortedStatusOptions() {
    return [...this.statusOptions].sort((a, b) =>
      numberSort(a.tableOrder, b.tableOrder, 'asc'),
    );
  }

  get sourceOptions() {
    return [
      {
        label: this.intl.t('source-options.vision'),
        value: 'VISION',
        description: this.intl.t('source-options.vision-description'),
        tableOrder: 1,
      },
      {
        label: this.intl.t('source-options.customer-request'),
        value: 'CUSTOMER REQUEST CURRENT',
        description: this.intl.t('source-options.customer-request-description'),
        tableOrder: 2,
      },
      {
        label: this.intl.t('source-options.operations'),
        value: 'OPERATIONS AND MAINTENANCE',
        description: this.intl.t('source-options.operations-description'),
        tableOrder: 3,
      },
    ];
  }

  get themeColorOptions() {
    return [
      { label: 'Grey', value: 'bg-gray-600' },
      { label: 'Yellow', value: 'bg-amber-600' },
      { label: 'Orange', value: 'bg-orange-600' },
      { label: 'Green', value: 'bg-green-600' },
      { label: 'Blue', value: 'bg-blue-600' },
      { label: 'Purple', value: 'bg-purple-600' },
      { label: 'Pink', value: 'bg-pink-600' },
      { label: 'Red', value: 'bg-red-600' },
    ];
  }

  retrievePermission(key: string) {
    if (!this.config) return;
    const permissionsObj = this.config[key];
    if (!permissionsObj) {
      return true;
    }
    if (permissionsObj.enabled === false) {
      return false;
    }
    const tenants = permissionsObj.tenants;
    if (!tenants) return true;
    return tenants.includes(String(this.orgId));
  }

  get groupingOptions() {
    return [
      {
        label: this.intl.t('_initiatives.status'),
        value: 'status',
        default: true,
      },
      {
        label: `${this.intl.t('_initiatives.progress')} ${capitalize(
          this.intl.t('_initiatives.update', {
            num: 1,
          }),
        )}`,
        value: 'updates',
      },
      {
        label: this.intl.t('_initiatives.product-manager'),
        value: 'productManager',
      },
      {
        label: this.intl.t('_initiatives.teams', {
          num: 1,
        }),
        value: 'team',
      },
      {
        label: this.intl.t('_initiatives.source', {
          num: 1,
        }),
        value: 'source',
      },
      {
        label: this.intl.t('_initiatives.themes', {
          num: 1,
        }),
        value: 'theme',
      },
      {
        label: this.intl.t('_initiatives.roadmap', {
          num: 1,
        }),
        value: 'roadmap',
      },
    ];
  }

  get reportingPeriodOptions() {
    return [
      {
        label: 'Day',
        value: 'DAY',
      },
      {
        label: 'Week',
        value: 'WEEK',
      },
      {
        label: 'Month',
        value: 'MONTH',
      },
      {
        label: 'Year',
        value: 'YEAR',
      },
      {
        label: "Doesn't matter",
        value: 'NONE',
      },
    ];
  }

  get trendOptions() {
    return [
      {
        label: 'Increase',
        value: 'UP',
      },
      {
        label: 'Decrease',
        value: 'DOWN',
      },
      {
        label: "Doesn't matter",
        value: 'NEUTRAL',
      },
    ];
  }

  get symbolOptions() {
    return [
      {
        label: 'Number',
        value: 'number',
      },
      {
        label: 'Percentage',
        value: 'percentage',
      },
      {
        label: 'Currency',
        value: 'currency',
      },
    ];
  }

  get healthOptions() {
    return [
      {
        value: STATUS_KEYS.DELAYED,
        label: this.intl.t('_initiatives.updates_delayed'),
      },
      {
        value: STATUS_KEYS.AT_RISK,
        label: this.intl.t('_initiatives.updates_at_risk'),
      },
      {
        value: STATUS_KEYS.NEEDS_REVIEW,
        label: this.intl.t('_initiatives.updates_needs_review'),
      },
      {
        value: STATUS_KEYS.ON_TRACK,
        label: this.intl.t('_initiatives.updates_on_track'),
      },
    ];
  }

  get goalStatusOptions() {
    return [
      {
        label: this.intl.t('_goals.status.true'),
        value: true,
      },
      {
        label: this.intl.t('_goals.status.false'),
        value: false,
      },
    ];
  }

  get columnOptions() {
    const columns = [
      {
        label: capitalize(this.intl.t('_initiatives.abbreviation')),
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'abbreviation',
        showVisibilityToggle: true,
        show: this.showInitiativeAbbrvFieldInQuickAddFormForQPQ,
      },
      {
        label: this.intl.t('_initiatives.status'),
        dropdownDataKey: 'statuses',
        groupingKey: 'statuses',
        showGrouping: true,
        showSorting: true,
        sortKey: 'status',
        sortType: 'alphabetic',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.progress'),
        dropdownDataKey: 'updates',
        groupingKey: 'progress',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'progress',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.product-manager'),
        dropdownDataKey: 'productManagers',
        groupingKey: 'productManagers',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'productManager',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.teams', {
          num: 3,
        }),
        dropdownDataKey: 'teams',
        groupingKey: 'teams',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'team',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('roadmap.title', {
          num: 3,
        }),
        class: 'min-w-[120px]',
        dropdownDataKey: 'roadmaps',
        groupingKey: 'roadmaps',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'roadmap',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.source', {
          num: 1,
        }),
        dropdownDataKey: 'sources',
        groupingKey: 'sources',
        sortKey: 'source',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.themes', {
          num: 3,
        }),
        dropdownDataKey: 'themes',
        groupingKey: 'themes',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'theme',
        showVisibilityToggle: true,
      },
      {
        label: capitalize(
          this.intl.t('_initiatives.goals', {
            num: 3,
          }),
        ),
        class:
          'min-w-[120px] max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap',
        dropdownDataKey: 'goals',
        groupingKey: 'goals',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'goal',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.impact'),
        class:
          'min-w-[120px] max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-right',
        sortKey: 'bon_value',
        showGrouping: false,
        showSorting: true,
        sortType: 'numeric',
        textAlignment: 'right',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.investment', {
          num: 1,
        }),
        class: 'min-w-[120px] text-right',
        sortKey: 'bon_investment',
        showGrouping: false,
        showSorting: true,
        sortType: 'numeric',
        textAlignment: 'right',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.returns', {
          num: 1,
        }),
        class: 'min-w-[120px] max-w-[200px] text-right',
        sortKey: 'bon_returns',
        showGrouping: false,
        showSorting: true,
        sortType: 'numeric',
        textAlignment: 'right',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.effort'),
        class:
          'min-w-[120px] max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-right',
        sortKey: 'person_weeks',
        showGrouping: false,
        showSorting: true,
        sortType: 'numeric',
        textAlignment: 'right',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.uncertainty', { num: 1 }),
        class:
          'min-w-[120px] max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-right',
        sortKey: 'person_weeks_only_uncertainty',
        showGrouping: false,
        showSorting: true,
        sortType: 'numeric',
        textAlignment: 'right',
        showVisibilityToggle: true,
      },
      {
        label: `${this.intl.t('_teams.title', { num: 1 })} ${this.intl.t('_initiatives.effort')}`,
        class:
          'min-w-[120px] max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-center',
        sortKey: 'team_effort',
        showGrouping: false,
        showSorting: true,
        sortType: 'numeric',
        show: false,
        showVisibilityToggle: true,
      },
      {
        label: `${this.intl.t('_initiatives.effort')} ${this.intl.t('_initiatives.details')}`,
        show: false,
        showGrouping: false,
        showSorting: false,
        showVisibilityToggle: true,
        textAlignment: 'right',
      },
      {
        label: this.intl.t('_initiatives.starting-week'),
        showGrouping: false,
        showSorting: true,
        sortType: 'date',
        sortKey: 'starting_week',
        class: '!max-w-fit',
        showVisibilityToggle: true,
      },
      {
        label: this.intl.t('_initiatives.duration'),
        groupingKey: 'durations',
        showGrouping: true,
        showSorting: true,
        sortType: 'numeric',
        sortKey: 'duration',
        showVisibilityToggle: true,
      },
    ];

    return columns;
  }

  get agentProviderOptions() {
    return [
      {
        label: 'n8n',
        value: 'N8N',
      },
      {
        label: 'Flowise',
        value: 'FLOWISE',
      },
      {
        label: 'Writer',
        value: 'WRITER',
        disabled: true,
      },
      {
        label: 'Sana',
        value: 'SANA',
        disabled: true,
      },
      {
        label: 'Langchain',
        value: 'LANGCHAIN',
        disabled: true,
      },
    ];
  }

  get ideaStatusOptions() {
    return [
      {
        label: this.intl.t('idea.status.new'),
        value: 'NEW',
        tableOrder: 1,
      },
      {
        label: this.intl.t('idea.status.parked'),
        value: 'PARKED',
        tableOrder: 2,
      },
      {
        label: this.intl.t('idea.status.long_list'),
        value: 'LONG_LIST',
        tableOrder: 3,
      },
      {
        label: this.intl.t('idea.status.short_list'),
        value: 'SHORT_LIST',
        tableOrder: 4,
      },
      {
        label: this.intl.t('idea.status.incomplete'),
        value: 'INCOMPLETE',
        tableOrder: 5,
      },
      {
        label: this.intl.t('idea.status.in_development'),
        value: 'IN_DEVELOPMENT',
        tableOrder: 6,
      },
      {
        label: this.intl.t('idea.status.in_review'),
        value: 'IN_REVIEW',
        tableOrder: 7,
      },
      {
        label: this.intl.t('idea.status.done'),
        value: 'DONE',
        tableOrder: 8,
      },
    ];
  }

  get payloadOptions() {
    return [
      {
        key: 'Access Token',
        value: '$access_token',
        type: 'fixed',
      },
      {
        key: `${this.intl.t('_initiatives.title', { num: 1 })} ID`,
        value: '$initiative_id',
        type: 'fixed',
      },
      {
        key: `${this.intl.t('dictionary.organisation')} ID`,
        value: '$tenant_id',
        type: 'fixed',
      },
      {
        key: 'User ID',
        value: '$user_id',
        type: 'fixed',
      },
      {
        key: 'Custom',
        value: 'ABC123',
        type: 'custom',
      },
    ];
  }
}
